import { NavMain } from '@/components/layout/nav-main'

// import { NavSecondary } from '@/components/layout/nav-secondary'
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useAuth } from '@/features/authentication/components/auth-provider.tsx'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { navItems } from '@/lib/route-mapping'
import { IconInnerShadowTop } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { useListMyCommunities } from '@/utils/api/membership-service'
import { ArrowRight, Loader2 } from 'lucide-react'

import * as React from 'react'
import { NavCollapsible } from '../nav-collapsible'
import { Link, useLocation } from '@tanstack/react-router'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth()
  const showAdminRoutes = isAdminUser(user)
  const isOrganisationAdmin = isOrganisationAdminUser(user)

  const mainNavItems = React.useMemo(() => {
    return navItems.navMain.filter((item) => {
      if (!item.roles) {
        return true
      }

      return item.roles.some((role) => {
        if (role === 'admin') return showAdminRoutes
        if (role === 'orgAdmin') return isOrganisationAdmin
        return false
      })
    })
  }, [isOrganisationAdmin, showAdminRoutes])

  return (
    <>
      {/* Use icon collapse so sidebar reserves space on desktop (prevents content overlap). */}
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                className="data-[slot=sidebar-menu-button]:!p-1.5"
              >
                <a href="#">
                  <IconInnerShadowTop className="!size-5" />
                  <span className="text-base font-semibold">UIBFS Membership</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <NavMain items={mainNavItems} />
          <NavCollapsible items={navItems.navCollapsible} />
          <MyCommunitiesNav />
          {showAdminRoutes && <NavCollapsible items={navItems.navSettings} />}

          {/* <NavSecondary items={navItems.navSecondary} className="mt-auto" /> */}
        </SidebarContent>
      </Sidebar>
    </>
  )
}

function MyCommunitiesNav() {
  const myCommunitiesQuery = useListMyCommunities(undefined, {
    query: { staleTime: 1000 * 60 * 3 },
  })
  const { pathname } = useLocation()
  const communities = myCommunitiesQuery.data?.data?.content ?? []
  const visibleCommunities = communities.slice(0, 3)
  const hasMore = communities.length > visibleCommunities.length

  if (!communities.length && !myCommunitiesQuery.isPending) {
    return null
  }

  return (
    <div className="mt-6 space-y-2 px-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        My communities
      </p>
      {myCommunitiesQuery.isPending ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {visibleCommunities.map((community) =>
            community.id ? (
              <Link
                to="/benefits/communities/$communityId"
                params={{ communityId: String(community.id) }}
                key={community.id}
                className={cn(
                  'rounded-md px-3 py-2 text-sm transition-colors',
                  'hover:text-sidebar-accent-foreground hover:bg-sidebar-accent focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-accent/60',
                  pathname === `/benefits/communities/${community.id}`
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold'
                    : 'text-muted-foreground'
                )}
              >
                {community.name ?? `Community ${community.id}`}
              </Link>
            ) : null
          )}
          {hasMore ? (
            <Link
              to="/benefits/communities"
              className="mt-1 flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-accent/60"
            >
              Show more communities
              <ArrowRight className="size-4 shrink-0" />
            </Link>
          ) : null}
        </div>
      )}
    </div>
  )
}
